import React, { useEffect, useState } from "react";
import {
  CheckCircle,
  Phone,
  Mail,
  MapPin,
  ArrowRight,
  ShieldCheck,
  TrendingUp,
  Headphones,
  Globe2,
} from "lucide-react";

export default function PartnerWithUsWallTexture() {
  const [ContactPageData, setContactPageData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [responseMessage, setResponseMessage] = useState({
    type: "",
    text: "",
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(
          "https://webadmin.naturx.co.in/api/get_extra_details",
        );
        const result = await response.json();

        if (result.status === 1) {
          setContactPageData(result.data);
        }
      } catch (error) {
        console.error(error);
      }
    };

    fetchData();
  }, []);

  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    category: "",
    message: "",
  });

  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!form.name) e.name = "Required";
    if (!form.company) e.company = "Required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = "Valid email required";
    if (!/^\d{10}$/.test(form.phone)) e.phone = "10 digit phone";
    if (!form.category) e.category = "Select category";
    return e;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    setResponseMessage({ type: "", text: "" });

    const e = validate();
    setErrors(e);

    if (Object.keys(e).length === 0) {
      setLoading(true);
      try {
        const payload = {
          name: form.name,
          phone: form.phone,
          email: form.email,
          company_name: form.company,
          category: form.category,
          msg: form.message,
        };

        const response = await fetch(
          "https://webadmin.naturx.co.in/api/save_partner",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
          },
        );

        const result = await response.json();

        if (response.ok) {
          setResponseMessage({
            type: "success",
            text: result.message || "Submitted successfully!",
          });
          // Reset form on success
          setForm({
            name: "",
            company: "",
            email: "",
            phone: "",
            category: "",
            message: "",
          });
        } else {
          setResponseMessage({
            type: "error",
            text: result.message || "Something went wrong. Please try again.",
          });
        }
      } catch (error) {
        console.error(error);
        setResponseMessage({
          type: "error",
          text: "Network error. Please check your connection.",
        });
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="bg-slate-50 text-slate-800 min-h-screen font-sans">
      {/* FORM & DETAILS SECTION */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 lg:py-8 py-4 relative z-20">
        <div className="grid lg:grid-cols-12 gap-6 items-start">
          {/* LEFT CONTACT CARDS (4 Cols) */}
          <div className="lg:col-span-5 space-y-4">
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200/80">
              <h3 className="text-lg font-bold text-slate-900 mb-4">
                Direct Contact Information
              </h3>
              <p className="text-sm text-slate-500 mb-6">
                Have questions before registering? Reach out to our partnership
                desk directly.
              </p>

              <div className="space-y-4">
                {/* Email */}
                <div className="flex items-start gap-4 p-4 rounded-xl bg-orange-50/50 border border-orange-100 transition hover:bg-orange-50">
                  <div className="bg-orange-500 text-white p-3 rounded-lg shrink-0">
                    <Mail size={20} />
                  </div>
                  <div>
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-orange-600">
                      Email Address
                    </h4>
                    <a
                      href={`mailto:${ContactPageData?.email}`}
                      className="text-sm font-medium text-slate-800 mt-0.5 block"
                    >
                      {ContactPageData?.email || "Loading..."}
                    </a>
                  </div>
                </div>

                {/* Phone */}
                <div className="flex items-start gap-4 p-4 rounded-xl bg-orange-50/50 border border-orange-100 transition hover:bg-orange-50">
                  <div className="bg-orange-500 text-white p-3 rounded-lg shrink-0">
                    <Phone size={20} />
                  </div>
                  <div>
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-orange-600">
                      Phone Number
                    </h4>
                    <a
                      href={`tel:${ContactPageData?.phone}`}
                      className="text-sm font-medium text-slate-800 mt-0.5 block"
                    >
                      {ContactPageData?.phone || "Loading..."}
                    </a>
                  </div>
                </div>

                {/* Location */}
                <div className="flex items-start gap-4 p-4 rounded-xl bg-orange-50/50 border border-orange-100 transition hover:bg-orange-50">
                  <div className="bg-orange-500 text-white p-3 rounded-lg shrink-0">
                    <MapPin size={20} />
                  </div>
                  <div>
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-orange-600">
                      Head Office
                    </h4>
                    <a
                      href={ContactPageData?.address_1_link}
                      target="_blank"
                      rel="noreferrer"
                      className="text-sm font-medium text-slate-800 mt-0.5 block leading-relaxed"
                    >
                      {ContactPageData?.address1 || "Loading..."}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT FORM CONTAINER (7 Cols) */}
          <div className="lg:col-span-7">
            <div className="bg-white border border-slate-200/80 rounded-2xl p-6">
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-slate-900">
                  Express Your Interest
                </h3>
                <p className="text-slate-500 text-sm mt-1">
                  Fill out the quick form below and our team will get back to
                  you within 24 hours.
                </p>
              </div>

              {responseMessage.text && (
                <div
                  className={`mb-4 p-4 rounded-xl text-sm font-medium ${
                    responseMessage.type === "success"
                      ? "bg-green-50 text-green-700 border border-green-200"
                      : "bg-red-50 text-red-700 border border-red-200"
                  }`}
                >
                  {responseMessage.text}
                </div>
              )}

              <form onSubmit={submit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-md font-semibold text-slate-950 mb-2">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      value={form.name}
                      className="w-full text-sm px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-orange-500 focus:ring-2 focus:ring-orange-100 outline-none transition"
                      placeholder="e.g. John Doe"
                      onChange={(e) =>
                        setForm({ ...form, name: e.target.value })
                      }
                    />
                    {errors.name && (
                      <p className="text-red-500 mt-1 text-xs">{errors.name}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-md font-semibold text-slate-950 mb-2">
                      Company Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      value={form.company}
                      className="w-full text-sm px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-orange-500 focus:ring-2 focus:ring-orange-100 outline-none transition"
                      placeholder="e.g. Acme Interiors"
                      onChange={(e) =>
                        setForm({ ...form, company: e.target.value })
                      }
                    />
                    {errors.company && (
                      <p className="text-red-500 mt-1 text-xs">
                        {errors.company}
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-md font-semibold text-slate-950 mb-2">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      value={form.email}
                      className="w-full text-sm px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-orange-500 focus:ring-2 focus:ring-orange-100 outline-none transition"
                      placeholder="john@example.com"
                      onChange={(e) =>
                        setForm({ ...form, email: e.target.value })
                      }
                    />
                    {errors.email && (
                      <p className="text-red-500 mt-1 text-xs">
                        {errors.email}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-md font-semibold text-slate-950 mb-2">
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      value={form.phone}
                      className="w-full text-sm px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-orange-500 focus:ring-2 focus:ring-orange-100 outline-none transition"
                      placeholder="10 digit number"
                      onChange={(e) =>
                        setForm({ ...form, phone: e.target.value })
                      }
                    />
                    {errors.phone && (
                      <p className="text-red-500 mt-1 text-xs">
                        {errors.phone}
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-md font-semibold text-slate-950 mb-2">
                    Select Partner Category{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={form.category}
                    className="w-full text-sm px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-orange-500 focus:ring-2 focus:ring-orange-100 outline-none transition"
                    onChange={(e) =>
                      setForm({ ...form, category: e.target.value })
                    }
                  >
                    <option value="">Choose category...</option>
                    <option value="Dealer">Dealer</option>
                    <option value="Designer">Designer</option>
                    <option value="Contractor">Contractor</option>
                  </select>
                  {errors.category && (
                    <p className="text-red-500 mt-1 text-xs">
                      {errors.category}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-md font-semibold text-slate-950 mb-2">
                    Message / Business Overview
                  </label>
                  <textarea
                    value={form.message}
                    className="w-full text-sm px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-orange-500 focus:ring-2 focus:ring-orange-100 outline-none transition min-h-[100px]"
                    placeholder="Tell us a bit about your experience or setup..."
                    onChange={(e) =>
                      setForm({ ...form, message: e.target.value })
                    }
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-4 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-semibold flex items-center justify-center gap-2 shadow-lg shadow-orange-500/25 transition-all duration-200 disabled:opacity-50"
                >
                  {loading ? "Submitting..." : "Submit Partnership Request"}{" "}
                  <ArrowRight size={18} />
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
